/** Jobs API — background subagent/job tracking. Backs the "Running tasks"
 *  section on the Schedules page. Mirrors schedules.ts (cookie-session auth via
 *  apiFetch; degrade-quietly on error). */
import { apiFetch } from "@/auth/token";
import { friendlyError } from "@/api/friendlyError";

export type JobStatus =
  | "pending"
  | "running"
  | "awaiting_approval"
  | "cancelling"
  | "succeeded"
  | "failed"
  | "cancelled"
  | string;

export interface JobSummary {
  id: string;
  type: string;
  status: JobStatus;
  error: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  artifact_id?: string | null;
  session_id?: string | null;
}

/** Cookie-session request init. Identity rides the HttpOnly cookie via
 *  apiFetch; no Authorization header. */
function reqInit(extra?: RequestInit): RequestInit {
  return {
    credentials: "include",
    ...extra,
    headers: {
      ...(extra?.headers ?? {}),
    },
  };
}

/** GET /v1/jobs — the user's jobs, newest first. Filterable by status/type.
 *  Returns [] on error so the tasks section degrades quietly. */
export async function listJobs(
  opts?: { status?: JobStatus; type?: string; limit?: number },
): Promise<JobSummary[]> {
  try {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.type) qs.set("type", opts.type);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    const r = await apiFetch(`/jobs${suffix}`, reqInit());
    if (!r.ok) return [];
    return (await r.json()) as JobSummary[];
  } catch {
    return [];
  }
}

/** POST /v1/jobs/{id}/cancel — soft cancel (pending/running → cancelling). */
export async function cancelJob(jobId: string): Promise<void> {
  const r = await apiFetch(`/jobs/${jobId}/cancel`, reqInit({ method: "POST" }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}
